// Pure logic for the staff job-sources page: board links to ATS and token,
// interval and time labels, the boards table's query string, and the running
// totals of a manual refresh. No React in here.

import {
  AtsPlatform,
  BoardAgencyFilter,
  BoardListParams,
  BoardSort,
  BoardStateFilter,
  IngestRunResult,
  IngestTrigger,
  JobSourcesOverview,
  SourceBoard,
} from '../types';
import { humanize, normalizeText, postedAgo } from './jobBoard';

// ---------------------------------------------------------------------------
// ATS and board tokens
// ---------------------------------------------------------------------------

/** Mirrors SUPPORTED_PLATFORMS in backend/apps/jobs/ingest/sources.py. */
export const ATS_PLATFORMS: AtsPlatform[] = ['greenhouse', 'lever', 'ashby', 'workable', 'gem', 'rippling', 'jazzhr', 'recruitee', 'jobvite', 'breezy'];

export function isAtsPlatform(value: unknown): value is AtsPlatform {
  return typeof value === 'string' && (ATS_PLATFORMS as string[]).includes(value);
}

// Mirrors TOKEN_RE in the same file.
const TOKEN_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,99}$/;

export function isValidToken(token: string): boolean {
  return TOKEN_RE.test(token);
}

export interface TokenExample {
  platform: AtsPlatform;
  /** The parts of a posting link around the token. */
  before: string;
  token: string;
  after: string;
}

/** Where the token sits in a posting link, one example per ATS. */
export const TOKEN_EXAMPLES: TokenExample[] = [
  { platform: 'greenhouse', before: 'https://job-boards.greenhouse.io/', token: 'acme', after: '/jobs/123' },
  { platform: 'lever', before: 'https://jobs.lever.co/', token: 'acme', after: '/...' },
  { platform: 'ashby', before: 'https://jobs.ashbyhq.com/', token: 'acme', after: '/...' },
  { platform: 'workable', before: 'https://apply.workable.com/', token: 'acme', after: '/...' },
  { platform: 'gem', before: 'https://jobs.gem.com/', token: 'acme', after: '/...' },
  { platform: 'rippling', before: 'https://ats.rippling.com/', token: 'acme', after: '/jobs/...' },
  { platform: 'jazzhr', before: 'https://', token: 'acme', after: '.applytojob.com/apply/...' },
  { platform: 'recruitee', before: 'https://', token: 'acme', after: '.recruitee.com/o/...' },
  { platform: 'jobvite', before: 'https://jobs.jobvite.com/', token: 'acme', after: '/job/...' },
  { platform: 'breezy', before: 'https://', token: 'acme', after: '.breezy.hr/p/...' },
];

/** ATS that give each company a subdomain, which is the board token. */
const SUBDOMAIN_BOARDS: [string, AtsPlatform][] = [
  ['.applytojob.com', 'jazzhr'],
  ['.recruitee.com', 'recruitee'],
  ['.breezy.hr', 'breezy'],
];
const VENDOR_SUBDOMAINS = new Set(['app', 'api', 'auth', 'docs', 'help', 'support', 'developer', 'marketing', 'resources', 'status', 'blog', 'careers']);

export interface BoardRef {
  platform: AtsPlatform;
  token: string;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

/** True when the text is a link rather than a bare token (tokens have no slash). */
export function looksLikeUrl(text: string): boolean {
  return text.includes('/');
}

/**
 * The ATS and board token in a posting or board link, or null when the link
 * is not on a supported ATS. The scheme is optional, so a link copied without
 * "https://" works too.
 *
 *   https://job-boards.greenhouse.io/acme/jobs/123   -> greenhouse, acme
 *   https://boards.greenhouse.io/embed/job_app?for=acme&token=123
 *   https://jobs.lever.co/acme/1f2e...               -> lever, acme
 *   https://jobs.ashbyhq.com/acme                    -> ashby, acme
 *   https://apply.workable.com/acme/j/ABC123/        -> workable, acme
 *   https://jobs.gem.com/acme/abc                    -> gem, acme
 *   https://ats.rippling.com/acme/jobs/uuid          -> rippling, acme
 *   https://acme.applytojob.com/apply/CODE/Title     -> jazzhr, acme
 *   https://acme.recruitee.com/o/some-role           -> recruitee, acme
 *   https://jobs.jobvite.com/acme/job/oAbC1fwX       -> jobvite, acme
 *   https://acme.breezy.hr/p/0123abcd-some-role      -> breezy, acme
 */
export function parseBoardUrl(text: string): BoardRef | null {
  const raw = (text ?? '').trim();
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const host = url.hostname.toLowerCase().replace(/^www\./, '');
  const segments = url.pathname.split('/').filter(Boolean).map(decodeSegment);

  let platform: AtsPlatform;
  let token = '';
  if (host === 'boards.greenhouse.io' || host === 'job-boards.greenhouse.io') {
    platform = 'greenhouse';
    // An embedded form names the board in its query string.
    token = segments[0] === 'embed' ? url.searchParams.get('for') ?? '' : segments[0] ?? '';
  } else if (host === 'boards-api.greenhouse.io') {
    platform = 'greenhouse';
    const at = segments.indexOf('boards');
    token = at >= 0 ? segments[at + 1] ?? '' : '';
  } else if (host === 'jobs.lever.co') {
    platform = 'lever';
    token = segments[0] ?? '';
  } else if (host === 'jobs.ashbyhq.com') {
    platform = 'ashby';
    token = segments[0] ?? '';
  } else if (host === 'apply.workable.com') {
    platform = 'workable';
    // "/j/<code>" and "/api/..." have no board name in front.
    token = segments[0] === 'j' || segments[0] === 'api' ? '' : segments[0] ?? '';
  } else if (host === 'jobs.gem.com') {
    platform = 'gem';
    token = segments[0] ?? '';
  } else if (host === 'ats.rippling.com') {
    platform = 'rippling';
    token = segments[0] ?? '';
  } else if (host === 'jobs.jobvite.com') {
    platform = 'jobvite';
    // Some career sites sit under "/careers/<name>/...".
    token = (segments[0] === 'careers' ? segments[1] : segments[0]) ?? '';
  } else {
    // The board is the subdomain: acme.applytojob.com, acme.recruitee.com, acme.breezy.hr.
    const hosted = SUBDOMAIN_BOARDS.find(([suffix]) => host.endsWith(suffix) && host.length > suffix.length);
    if (!hosted) return null;
    platform = hosted[1];
    token = host.slice(0, -hosted[0].length);
    // The vendor's own sites (app.breezy.hr, docs.recruitee.com) are not boards.
    if (token.includes('.') || VENDOR_SUBDOMAINS.has(token)) return null;
  }
  return isValidToken(token) ? { platform, token } : null;
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** "1 listing", "2 listings", "1,204 jobs". */
export function countLabel(count: number, singular: string, plural: string = `${singular}s`): string {
  return `${formatCount(count)} ${count === 1 ? singular : plural}`;
}

export function formatCount(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString('en-US') : '0';
}

/** Limits of the refresh interval, in hours (the API's own). */
export const MIN_INTERVAL_HOURS = 1;
export const MAX_INTERVAL_HOURS = 336;

/**
 * How often the scheduler calls the API, in hours: the cron in
 * .github/workflows/ingest-jobs.yml, as docs/api-contract.md describes it. The
 * API does not report it, so it is repeated here. It is the finest step a
 * refresh interval can have; a shorter interval reads boards on every call.
 */
export const SCHEDULER_EVERY_HOURS: number = 2;

/** Quick choices for a refresh interval, in hours: steps of the scheduler's own. */
export const INTERVAL_PRESETS: number[] = [2, 4, 6, 12, 24];

/** 1 -> "1 hour", 6 -> "6 hours", 48 -> "2 days", 36 -> "36 hours". */
export function intervalLabel(hours: number): string {
  if (hours >= 48 && hours % 24 === 0) return `${hours / 24} days`;
  return hours === 1 ? '1 hour' : `${hours} hours`;
}

/** "every hour", "every 2 hours": how often the scheduler checks for due boards. */
export function schedulerCadence(): string {
  return SCHEDULER_EVERY_HOURS === 1 ? 'every hour' : `every ${intervalLabel(SCHEDULER_EVERY_HOURS)}`;
}

/**
 * The note under an interval field: its limits, or, for an interval shorter
 * than the scheduler's own step, what will actually happen.
 */
export function intervalHint(hours: number | null): string {
  if (hours !== null && hours >= MIN_INTERVAL_HOURS && hours < SCHEDULER_EVERY_HOURS) {
    return `The scheduler only checks ${schedulerCadence()}.`;
  }
  return `${MIN_INTERVAL_HOURS} to ${MAX_INTERVAL_HOURS}`;
}

/** The interval a board is read on: its own when set, otherwise the global one. */
export function boardInterval(own: number | null | undefined, globalHours: number): { hours: number; own: boolean } {
  return typeof own === 'number' ? { hours: own, own: true } : { hours: globalHours, own: false };
}

/**
 * A whole number typed into a field, or null when the text is not one. Ranges
 * are left to the API, whose message is shown by the field.
 */
export function parseWholeNumber(text: string): number | null {
  const value = text.trim();
  if (!/^-?\d+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

/** ISO datetime -> "5h ago"; "never" when the board has not been read. */
export function readAgo(value: string | null | undefined, now: number = Date.now()): string {
  return postedAgo(value, now) || 'never';
}

/** 0.42 -> "0.4s", 41.2 -> "41s", 125 -> "2m 5s". */
export function durationLabel(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return '';
  if (seconds < 10) return `${Math.round(seconds * 10) / 10}s`;
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const whole = Math.round(seconds);
  const rest = whole % 60;
  return rest ? `${Math.floor(whole / 60)}m ${rest}s` : `${Math.floor(whole / 60)}m`;
}

const DISCOVERY_LABELS: Record<string, string> = {
  seed: 'Seed list',
  manual: 'Added by hand',
  extension: 'Extension',
};

export function discoveryLabel(value: string): string {
  return DISCOVERY_LABELS[value] ?? (value ? humanize(value) : 'Unknown');
}

/**
 * Why a board carries the agency flag, for its tooltip: staff's own decision,
 * or what a refresh saw in the postings.
 */
export function agencyHint(board: Pick<SourceBoard, 'agency_source' | 'agency_reason'>): string {
  if (board.agency_source === 'manual') return 'Marked as a staffing agency by staff. Refreshes keep it.';
  return board.agency_reason ? `Flagged automatically: ${board.agency_reason}` : 'Flagged automatically';
}

const TRIGGER_LABELS: Record<IngestTrigger, string> = {
  schedule: 'Schedule',
  manual: 'Manual',
  command: 'Command line',
};

export function triggerLabel(value: string): string {
  return TRIGGER_LABELS[value as IngestTrigger] ?? humanize(value);
}

// ---------------------------------------------------------------------------
// The boards table and its query string
// ---------------------------------------------------------------------------

export const BOARDS_PAGE_SIZE = 25;

export const BOARD_STATE_LABELS: Record<BoardStateFilter, string> = {
  active: 'On',
  off: 'Off',
  failing: 'Failing',
  due: 'Due now',
  never: 'Never read',
};

export const BOARD_STATES = Object.keys(BOARD_STATE_LABELS) as BoardStateFilter[];

export const BOARD_AGENCY_LABELS: Record<BoardAgencyFilter, string> = {
  yes: 'Staffing agencies',
  no: 'Direct employers',
};

export const BOARD_AGENCY_FILTERS = Object.keys(BOARD_AGENCY_LABELS) as BoardAgencyFilter[];

export const BOARD_SORT_LABELS: Record<BoardSort, string> = {
  name: 'Company name',
  jobs: 'Most jobs',
  oldest: 'Oldest read first',
  newest: 'Newest read first',
};

export const BOARD_SORTS = Object.keys(BOARD_SORT_LABELS) as BoardSort[];

export interface BoardListState {
  q: string;
  /** '' is every ATS. */
  platform: AtsPlatform | '';
  /** '' is agencies and direct employers alike. */
  agency: BoardAgencyFilter | '';
  /** '' is every board. */
  state: BoardStateFilter | '';
  sort: BoardSort;
  /** 1-based. */
  page: number;
}

export const DEFAULT_BOARD_LIST: BoardListState = { q: '', platform: '', agency: '', state: '', sort: 'name', page: 1 };

/** Read the table state out of a query string; anything malformed is its default. */
export function parseBoardListQuery(query: string | URLSearchParams): BoardListState {
  const params = typeof query === 'string' ? new URLSearchParams(query) : query;
  const platform = params.get('platform');
  const agency = params.get('agency') as BoardAgencyFilter | null;
  const state = params.get('state') as BoardStateFilter | null;
  const sort = params.get('sort') as BoardSort | null;
  const page = params.get('page') ?? '';
  return {
    q: normalizeText(params.get('q') ?? ''),
    platform: isAtsPlatform(platform) ? platform : '',
    agency: agency && BOARD_AGENCY_FILTERS.includes(agency) ? agency : '',
    state: state && BOARD_STATES.includes(state) ? state : '',
    sort: sort && BOARD_SORTS.includes(sort) ? sort : 'name',
    page: /^\d+$/.test(page) && Number(page) >= 1 && Number.isSafeInteger(Number(page)) ? Number(page) : 1,
  };
}

/** The query string for a table state, "" or "?a=b". Defaults are left out. */
export function boardListSearch(state: BoardListState): string {
  const params = new URLSearchParams();
  if (state.q) params.set('q', state.q);
  if (state.platform) params.set('platform', state.platform);
  if (state.agency) params.set('agency', state.agency);
  if (state.state) params.set('state', state.state);
  if (state.sort !== 'name') params.set('sort', state.sort);
  if (state.page > 1) params.set('page', String(state.page));
  const text = params.toString();
  return text ? `?${text}` : '';
}

/** Parameters for one page of `GET /jobs/admin/boards/`. */
export function boardListParams(state: BoardListState): BoardListParams {
  const params: BoardListParams = { sort: state.sort, page: state.page, page_size: BOARDS_PAGE_SIZE };
  if (state.q) params.q = state.q;
  if (state.platform) params.platform = state.platform;
  if (state.agency) params.agency = state.agency;
  if (state.state) params.state = state.state;
  return params;
}

export function boardPages(count: number): number {
  return Math.max(1, Math.ceil(count / BOARDS_PAGE_SIZE));
}

// ---------------------------------------------------------------------------
// A manual refresh: several calls added up
// ---------------------------------------------------------------------------

export interface RunProgress {
  /** Calls answered so far. */
  calls: number;
  boards: number;
  failed: number;
  created: number;
  updated: number;
  deactivated: number;
  /** Boards still to read; null before the first answer. */
  remaining: number | null;
  /** Boards left for the scheduled refresh because their feed is too slow for one call. */
  deferred: number;
  seconds: number;
}

export const EMPTY_RUN_PROGRESS: RunProgress = {
  calls: 0,
  boards: 0,
  failed: 0,
  created: 0,
  updated: 0,
  deactivated: 0,
  remaining: null,
  deferred: 0,
  seconds: 0,
};

const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/**
 * Add one call's answer to the running totals.
 *
 * Refreshing the due boards, `remaining` is the API's own count of boards
 * still due. Refreshing everything, the API counts every board as due on each
 * call, so its `remaining` only describes that one call; pass `target` (the
 * number of boards the whole pass has to read) and the count is kept here.
 */
export function addRunResult(progress: RunProgress, result: IngestRunResult, target?: number | null): RunProgress {
  const boards = progress.boards + num(result.boards);
  // Every call meets the same slow boards again; they are counted once.
  const deferred = Math.max(progress.deferred, num(result.deferred));
  return {
    calls: progress.calls + 1,
    boards,
    failed: progress.failed + num(result.failed),
    created: progress.created + num(result.created),
    updated: progress.updated + num(result.updated),
    deactivated: progress.deactivated + num(result.deactivated),
    remaining: typeof target === 'number' ? Math.max(0, target - boards - deferred) : num(result.remaining),
    deferred,
    seconds: progress.seconds + num(result.seconds),
  };
}

/** Active boards on the enabled ATS: what "refresh everything" reads. */
export function activeBoardsToRead(overview: JobSourcesOverview | null | undefined): number {
  const platforms = overview?.platforms ?? [];
  const enabled = platforms.filter((platform) => platform.enabled);
  return enabled.reduce((total, platform) => total + num(platform.active_boards), 0);
}

/** True when another call is needed after this answer. */
export function runShouldContinue(progress: RunProgress, last: IngestRunResult): boolean {
  // A call that read nothing will not get further by being repeated.
  return (progress.remaining ?? 0) > 0 && num(last.boards) > 0;
}
